import { useEffect, useRef, useState } from 'react';
import { FileText, Paperclip, Upload, X } from 'lucide-react';
import type {
  EventVendorRecipient,
  SendVendorEmailResult,
  VendorEmailAttachmentInput,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { RichTextEditor, SearchableListSelect } from '../common';
import { useSendVendorEmail } from '../../hooks/useEvents';
import { getAttachmentUploadUrl, listEntityAttachments } from '../../api/entity-attachments';
import type { EntityAttachment } from '@peles/shared';

const MAX_ATTACHMENT_TOTAL_BYTES = 7 * 1024 * 1024;

interface PendingAttachment extends VendorEmailAttachmentInput {
  sizeBytes: number | null;
}

export interface ComposeVendorEmailModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Selected vendors — all must already have a resolved email. */
  recipients: EventVendorRecipient[];
}

/**
 * Compose an email to the selected event vendors: subject, rich-text body,
 * attachments picked from the event's documents or uploaded from the device.
 * Sent per vendor from the system sender with Reply-To the logged-in user.
 */
export function ComposeVendorEmailModal({
  open,
  onClose,
  eventId,
  recipients,
}: ComposeVendorEmailModalProps) {
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [eventDocuments, setEventDocuments] = useState<EntityAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<SendVendorEmailResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendEmail = useSendVendorEmail(eventId);

  useEffect(() => {
    if (!open) return;
    setSubject('');
    setBodyHtml('');
    setAttachments([]);
    setError(null);
    setResult(null);
    // Event documents (S3-backed only — links can't be attached to an email).
    listEntityAttachments('EVENT', eventId)
      .then((docs) => setEventDocuments(docs.filter((doc) => doc.s3Key && doc.fileName)))
      .catch(() => setEventDocuments([]));
  }, [open, eventId]);

  const totalBytes = attachments.reduce((sum, a) => sum + (a.sizeBytes ?? 0), 0);

  function addAttachment(next: PendingAttachment) {
    setAttachments((prev) => (prev.some((a) => a.s3Key === next.s3Key) ? prev : [...prev, next]));
  }

  function handlePickEventDocument(attachmentId: string) {
    const doc = eventDocuments.find((d) => d.id === attachmentId);
    if (!doc?.s3Key || !doc.fileName) return;
    addAttachment({
      s3Key: doc.s3Key,
      fileName: doc.fileName,
      mimeType: doc.mimeType ?? 'application/octet-stream',
      sizeBytes: doc.fileSizeBytes ?? null,
    });
  }

  async function handleDeviceUpload(file: File) {
    setError(null);
    if (totalBytes + file.size > MAX_ATTACHMENT_TOTAL_BYTES) {
      setError('Attachments exceed the 7MB email limit.');
      return;
    }
    setUploading(true);
    try {
      const mimeType = file.type || 'application/octet-stream';
      const { uploadUrl, s3Key } = await getAttachmentUploadUrl(file.name, mimeType);
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });
      if (!response.ok) throw new Error('Upload failed');
      addAttachment({ s3Key, fileName: file.name, mimeType, sizeBytes: file.size });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function handleSend() {
    setError(null);
    sendEmail.mutate(
      {
        vendorIds: recipients.map((r) => r.vendorId),
        subject: subject.trim(),
        bodyHtml,
        attachments: attachments.map(({ s3Key, fileName, mimeType }) => ({
          s3Key,
          fileName,
          mimeType,
        })),
      },
      {
        onSuccess: (sendResult) => setResult(sendResult),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to send the email'),
      },
    );
  }

  const bodyIsEmpty = !bodyHtml.replace(/<[^>]*>/g, '').trim();
  const canSend =
    recipients.length > 0 && !!subject.trim() && !bodyIsEmpty && !sendEmail.isPending && !uploading;

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Email Vendors"
      size="lg"
      footer={
        result ? (
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            data-testid="close-after-send"
          >
            Done
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="send-vendor-email"
            >
              {sendEmail.isPending
                ? 'Sending…'
                : `Send to ${recipients.length} vendor${recipients.length === 1 ? '' : 's'}`}
            </button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-3" data-testid="send-result">
          <p className="text-sm text-green-700">
            Sent to {result.sent.length} vendor{result.sent.length === 1 ? '' : 's'}.
          </p>
          {result.skipped.length > 0 && (
            <div className="text-sm text-amber-700">
              <p className="font-medium">Skipped:</p>
              <ul className="list-disc ml-5">
                {result.skipped.map((skip) => {
                  const vendor = recipients.find((r) => r.vendorId === skip.vendorId);
                  return (
                    <li key={skip.vendorId}>
                      {vendor?.businessName ?? skip.vendorId} — {skip.reason}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1">To</p>
            <div className="flex flex-wrap gap-1.5" data-testid="recipient-chips">
              {recipients.map((recipient) => (
                <span
                  key={recipient.vendorId}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full"
                >
                  {recipient.businessName}
                  <span className="text-primary-400">({recipient.email})</span>
                </span>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Each vendor gets an individual email — replies go to your login email.
            </p>
          </div>

          <div>
            <label
              htmlFor="vendor-email-subject"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Subject
            </label>
            <input
              id="vendor-email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              data-testid="vendor-email-subject"
            />
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1">Message</p>
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder="Write your message…"
              ariaLabel="Email body"
              disabled={sendEmail.isPending}
            />
          </div>

          <div className="space-y-2">
            <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <Paperclip size={14} />
              Attachments
            </p>
            {attachments.length > 0 && (
              <ul className="space-y-1" data-testid="attachment-list">
                {attachments.map((attachment) => (
                  <li
                    key={attachment.s3Key}
                    className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
                  >
                    <FileText size={14} className="text-gray-400 shrink-0" />
                    <span className="flex-1 truncate">{attachment.fileName}</span>
                    {attachment.sizeBytes != null && (
                      <span className="text-xs text-gray-400">
                        {(attachment.sizeBytes / 1024 / 1024).toFixed(2)} MB
                      </span>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove ${attachment.fileName}`}
                      onClick={() =>
                        setAttachments((prev) => prev.filter((a) => a.s3Key !== attachment.s3Key))
                      }
                      className="p-0.5 text-gray-400 hover:text-red-600"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div data-testid="event-document-picker">
                <SearchableListSelect
                  items={eventDocuments.filter(
                    (doc) => !attachments.some((a) => a.s3Key === doc.s3Key),
                  )}
                  value={null}
                  onChange={(picked) => {
                    if (typeof picked === 'string' && picked) handlePickEventDocument(picked);
                  }}
                  getValue={(doc) => doc.id}
                  getLabel={(doc) => doc.fileName ?? 'Document'}
                  placeholder="Add from event documents…"
                  ariaLabel="Add from event documents"
                  clearable={false}
                  emptyMessage="No documents on this event yet."
                />
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-200 border-dashed rounded-lg hover:bg-gray-50 disabled:opacity-50"
                data-testid="upload-from-device"
              >
                <Upload size={14} />
                {uploading ? 'Uploading…' : 'Upload from device'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                data-testid="device-file-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleDeviceUpload(file);
                  e.target.value = '';
                }}
              />
            </div>
            {totalBytes > 0 && (
              <p className="text-xs text-gray-500">
                Total {(totalBytes / 1024 / 1024).toFixed(2)} MB of 7 MB
              </p>
            )}
          </div>
        </div>
      )}
    </EntityModal>
  );
}
